import { css } from '@emotion/react'
import { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { types, flow, getEnv } from 'mobx-state-tree'
import _ from 'lodash'
import { withRouterProps } from './utils'
import { API } from './api'
import { Logout } from './views'
import { Loading, LoadingMessage } from './components'

const { model, enumeration } = types

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const AUTH_CHECK_INTERVAL_MS = 1100
const AUTH_CHECK_RETRY_LIMIT = 30

const authCheck = async state => {
  let checkStatus = false
  let retryCount = 0

  while (checkStatus === false) {
    const response = await API.get('authTracker')
    checkStatus = _.get(response.data, 'complete', false)

    if (!checkStatus) {
      retryCount = retryCount + 1
      await sleep(AUTH_CHECK_INTERVAL_MS)
    }

    const authTrackerStatus = _.get(response.data, 'status', 'loading')
    if (authTrackerStatus === 'ERROR') {
      const authTrackerErrorMessage = _.get(response.data, 'errorDetails.message', '')
      if (authTrackerErrorMessage.length > 0) {
        state.setErrorMessage(authTrackerErrorMessage)
        throw new Error(authTrackerErrorMessage)
      } else {
        throw new Error('Error during auth process')
      }
    }

    if (retryCount > AUTH_CHECK_RETRY_LIMIT) {
      throw new Error(`Retry limit exceeded (${AUTH_CHECK_RETRY_LIMIT})`)
    }
  }
}

const State = model({
  status: enumeration(['loading', 'done', 'error']),
  tokenExists: false,
  errorMessage: '',
}).actions(self => ({
  afterCreate: flow(function* () {
    self.status = 'loading'
    const { store, navigate, location } = getEnv(self)
    const params = new URLSearchParams(location?.search)
    const { auth, getConfig, getFeatures, getAccount, getLicenseStatus } = store

    try {
      if (params.get('sso') && params.get('signature') && params.get('recordKeeper')) {
        self.tokenExists = true

        yield auth.authenticate(
          params.get('sso'),
          params.get('signature'),
          params.get('recordKeeper')
        )
      }

      yield authCheck(self)

      yield Promise.all([getConfig(), getFeatures(), getAccount()])

      const { config } = store

      // Check retail access before rendering authenticated routes.
      if (config.isRetail) {
        yield getLicenseStatus()
      }

      // Discontinue and managed-account upgrade flows land on product selection.
      if (config.template.landingPage === 'productSelect') {
        navigate('/product-select', { replace: true })
        auth.authorize()
        self.status = 'done'
        return
      }

      // Onboarding creates a plan, so members without plan access skip it.
      if (!config.onBoarded && store.hasPlanAccess) {
        navigate('/welcome', { replace: true })
      }

      if (config.onBoarded && config.requireToAcceptTerms) {
        navigate('/welcome/reaccept-terms', { replace: true })
      }

      auth.authorize()
      self.status = 'done'
    } catch (err) {
      console.error(err)
      self.status = 'error'
    }
  }),
  setErrorMessage(errorMessage) {
    self.errorMessage = errorMessage
  },
}))

class AuthCheck extends Component {
  constructor(props) {
    super(props)
    const { store, navigate, location } = props
    this.state = State.create({ status: 'loading' }, { store, navigate, location })
  }

  render() {
    if (this.state.status === 'error') {
      return <Logout errorMessage={this.state.errorMessage} />
    }

    return (
      <div
        css={css`
          padding: 20px;
        `}>
        <LoadingMessage />
        <Loading />
      </div>
    )
  }
}

export default withRouterProps(inject('store')(observer(AuthCheck)))
